import {
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  OverflowTooltip,
  Space,
  TextLabel,
  Input,
} from '@tonic-one/react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  InputChildRef,
  InputWithValid,
} from 'src/components/Common/Form/InputWithValid';
import { ValidProps } from 'src/types';
import {
  validateEmail,
  validateHpuName,
  validateIsNotWhitespace,
} from 'src/utils/validate';
import { useOrgStore } from '../../../../../models/useOrgModel';

export const ManualAddUserModal = React.memo(
  (props: {
    allData: string[];
    visible: boolean;
    onSave: (value: string[]) => void;
    onClose: () => void;
    title: string;
  }) => {
    const { t } = useTranslation();
    const [firstName, setFirstName] = React.useState('');
    const [middleName, setMiddleName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [emailAddress, setEmailAddress] = React.useState('');
    const firstNameChildRef = useRef<InputChildRef>(null);
    const lastNameChildRef = useRef<InputChildRef>(null);
    const emailAddressChildRef = useRef<InputChildRef>(null);
    const { orgId } = useOrgStore();
    const validateEmailAddress: ValidProps[] = [
      {
        validMethod: validateEmail,
        errMsg: t('common.error.invalidEmailAddress'),
      },
    ];

    const validateFirstNameEmpty: ValidProps[] = [
      {
        validMethod: validateIsNotWhitespace,
        errMsg: t('globalSetting.highProfileUsers.firstName.validator.empty'),
      },
      {
        validMethod: validateHpuName,
        errMsg: t('common.error.invalidFormat'),
      },
    ];

    const validateLastNameEmpty: ValidProps[] = [
      {
        validMethod: validateIsNotWhitespace,
        errMsg: t('globalSetting.highProfileUsers.lastName.validator.empty'),
      },
      {
        validMethod: validateHpuName,
        errMsg: t('common.error.invalidFormat'),
      },
    ];

    function resetInputs() {
      setEmailAddress('');
      setFirstName('');
      setMiddleName('');
      setLastName('');
    }

    return (
      <Modal
        closeOnEsc
        closeOnOutsideClick
        isClosable
        isOpen={props.visible}
        onClose={() => {
          props.onClose();
          resetInputs();
        }}
        size="sm"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <OverflowTooltip>{props.title}</OverflowTooltip>
          </ModalHeader>
          <ModalBody>
            <Grid templateColumns="min-content auto" columnGap="3x" rowGap="3x">
              <Grid>
                <TextLabel whiteSpace="nowrap">
                  {t('globalSetting.highProfileUsers.column.firstName')}:
                  <Space color="red:50">*</Space>
                </TextLabel>
              </Grid>
              <Grid>
                <InputWithValid
                  width={300}
                  ref={firstNameChildRef}
                  value={firstName}
                  onChange={setFirstName}
                  validates={validateFirstNameEmpty}
                  maxLength={64}
                  placeholder={t(
                    'globalSetting.highProfileUsers.column.firstName'
                  )}
                />
              </Grid>
              <Grid>
                <TextLabel whiteSpace="nowrap">
                  {t('globalSetting.highProfileUsers.column.middleName')}:
                </TextLabel>
              </Grid>
              <Grid>
                <Input
                  width={300}
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  maxLength={64}
                  placeholder={t(
                    'globalSetting.highProfileUsers.column.middleName'
                  )}
                />
              </Grid>
              <Grid>
                <TextLabel whiteSpace="nowrap">
                  {t('globalSetting.highProfileUsers.column.lastName')}:
                  <Space color="red:50">*</Space>
                </TextLabel>
              </Grid>
              <Grid>
                <InputWithValid
                  width={300}
                  ref={lastNameChildRef}
                  value={lastName}
                  onChange={setLastName}
                  validates={validateLastNameEmpty}
                  maxLength={64}
                  placeholder={t(
                    'globalSetting.highProfileUsers.column.lastName'
                  )}
                />
              </Grid>
              <Grid>
                <TextLabel whiteSpace="nowrap">
                  {t('globalSetting.highProfileUsers.column.emailAddress')}:
                </TextLabel>
              </Grid>
              <Grid>
                <InputWithValid
                  width={300}
                  ref={emailAddressChildRef}
                  value={emailAddress}
                  onChange={setEmailAddress}
                  validates={validateEmailAddress}
                  maxLength={256}
                  placeholder={t(
                    'globalSetting.highProfileUsers.column.emailAddress'
                  )}
                />
              </Grid>
            </Grid>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              minWidth="20x"
              onClick={async () => {
                /**
                 * if children has errorMsg,then not save
                 */

                await firstNameChildRef.current?.validate(firstName);
                if (firstNameChildRef.current?.errorMsg !== undefined) return;

                await lastNameChildRef.current?.validate(lastName);
                if (lastNameChildRef.current?.errorMsg !== undefined) return;

                if (emailAddress) {
                  await emailAddressChildRef.current?.validate(emailAddress);
                  if (emailAddressChildRef.current?.errorMsg !== undefined) {
                    return;
                  }
                }
                props.onClose();
                props.onSave([
                  `${firstName}|${middleName}|${lastName}|${emailAddress}|${orgId}`,
                ]);
                resetInputs();
              }}
            >
              {t('common.button.save')}
            </Button>
            <Space width="2x" />
            <Button minWidth="20x" onClick={props.onClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
);
